import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { Observable, take } from 'rxjs';
import { OrderSummary, OrderSummaryItem } from '@shared/component/payments/components/order-summary/order-summary.type';
import { CommonModule } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { BusinessPlanService } from '@app/modules/account-settings/pages/business-profile/services/business-plan.service';
import { LetModule } from '@ngrx/component';
import { CurrencyService } from '@modules/payment/services/currency.service';
import {
  ExtraJobBillingPreviewResult,
  JobBillingPreviewResult,
  PaymentCheckoutContextResult,
} from '@generated/graphql';
import { Order } from "@modules/order/models/order.model";
import { SharePipesModule } from "@shared/share-pipes/share-pipes.module";
import { OrderData } from "@shared/component/payments/components/order-summary/order-summary.type";
import { OrderSummaryFactory } from "@shared/component/payments/components/order-summary/order-summary.factory";

type BillingPreviewData =
  | NonNullable<JobBillingPreviewResult['jobBillingPreview']>
  | NonNullable<ExtraJobBillingPreviewResult['extraJobBillingPreview']>
  | NonNullable<PaymentCheckoutContextResult['paymentCheckoutContext']>;

@Component({
  selector: 'app-order-summary',
  standalone: true,
  templateUrl: './order-summary.component.html',
  styleUrls: ['./order-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatDividerModule,
    LetModule,
    SharePipesModule
  ],
})
export class OrderSummaryComponent {
  @Input() set mode(value: 'default' | 'checkout') {
    this._mode = value ?? 'default';
    this.updateCurrencyExchangeRate();
    this.recalculateEqualToUsdAmount();
    this.cdr.markForCheck();
  }
  @Input() set billingPreview(value: BillingPreviewData | null) {
    this._billingPreview = value;
    this.recalculateEqualToUsdAmount();
    this.cdr.markForCheck();
  }
  @Input() set data(value: OrderData) {
    this.setOrderData(value);
  }
  order: Order;
  orderSummary: OrderSummary | null = null;
  currencyExchangeRate: number | null = null;
  equalToUsdAmount: number | null = null;
  private currentCurrency: string | null = null;
  private _billingPreview: BillingPreviewData | null = null;
  private _mode: 'default' | 'checkout' = 'default';

  constructor(
    private readonly orderSummaryFactory: OrderSummaryFactory,
    private readonly _businessPlanService: BusinessPlanService,
    private readonly currencyService: CurrencyService,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  get businessPlan$() {
    return this._businessPlanService.businessPlan$;
  }

  get billingPreview(): BillingPreviewData | null {
    return this._billingPreview;
  }

  get isCheckoutMode(): boolean {
    return this._mode === 'checkout';
  }

  get summaryCurrency(): string {
    return this.billingPreview?.orderCurrency ?? this.orderSummary?.currency ?? '';
  }

  get supportedCountry() {
    return this.orderSummary?.supportedCountry ?? null;
  }

  get showPromotionDiscountRow(): boolean {
    return this.promotionDiscountAmount > 0;
  }

  get items(): OrderSummaryItem[] {
    const previewLines = this.billingPreview?.lines ?? [];

    if (this.isCheckoutMode) {
      return previewLines.map((line) => ({
        name: line.name,
        rate: line.rate,
        quantity: line.quantity,
        amount: line.amount,
        isContactToClient: line.isContactToClient,
        isSubscription: line.isSubscription,
        billingCycleLabel: line.billingCycleLabel ?? null,
      }));
    }

    if (previewLines.length) {
      return previewLines.map((line) => ({
        name: line.name,
        rate: line.rate,
        quantity: line.quantity,
        amount: line.amount,
        isContactToClient: line.isContactToClient,
        isSubscription: line.isSubscription,
        billingCycleLabel: line.billingCycleLabel ?? null,
      }));
    }

    return this.orderSummary?.items ?? [];
  }

  get shouldShowPendingPricingSummary(): boolean {
    const totalAmount = this.billingPreview?.totalAmount ?? this.orderSummary?.totalAmount ?? 0;

    return totalAmount === 0 && this.items.some((item) => item.isContactToClient);
  }

  get subtotalAmount(): number {
    if (this.isCheckoutMode) {
      return this.checkoutBaseSubtotalAmount;
    }

    return this.billingPreview?.subtotal ?? this.orderSummary?.subTotalAmount ?? 0;
  }

  get promotionDiscountAmount(): number {
    if (this.isCheckoutMode) {
      return this.checkoutDiscountAmount;
    }

    return this.order?.discountAmount ?? 0;
  }

  get taxAmount(): number {
    if (this.isCheckoutMode) {
      return this.billingPreview?.tax ?? 0;
    }

    return this.billingPreview?.tax ?? this.orderSummary?.taxAmount ?? 0;
  }

  get officialFeeAmount(): number {
    if (this.isCheckoutMode) {
      return this.billingPreview?.officialFee ?? 0;
    }

    return this.billingPreview?.officialFee ?? this.orderSummary?.officialFee ?? 0;
  }

  get totalAmount(): number {
    if (this.isCheckoutMode) {
      return this.billingPreview?.totalAmount ?? 0;
    }

    return this.billingPreview?.totalAmount ?? this.orderSummary?.data.order?.totalAmount ?? this.orderSummary?.totalAmount ?? 0;
  }

  private setOrderData(data: OrderData): void {
    this.orderSummary = data ? this.orderSummaryFactory.create(data) : null;
    this.order = this.orderSummary?.data?.order ?? null;

    this.updateCurrencyExchangeRate();
    this.recalculateEqualToUsdAmount();
    this.cdr.markForCheck();
  }

  private updateCurrencyExchangeRate(): void {
    const currency = this.summaryCurrency || null;

    if (this.isCheckoutMode) {
      this.currentCurrency = currency;
      this.currencyExchangeRate = null;
      this.equalToUsdAmount = null;
      return;
    }

    if (!currency || currency === 'USD') {
      this.currentCurrency = currency;
      this.currencyExchangeRate = null;
      this.equalToUsdAmount = null;
      return;
    }

    if (currency === this.currentCurrency && this.currencyExchangeRate !== null) {
      return;
    }

    this.currentCurrency = currency;
    this.currencyExchangeRate = null;

    this.currencyService.getCurrencyExchangeRate(currency).pipe(
      take(1),
    ).subscribe(rate => {
      this.currencyExchangeRate = rate;
      this.recalculateEqualToUsdAmount();
      this.cdr.markForCheck();
    });
  }

  private recalculateEqualToUsdAmount(): void {
    if (!this.orderSummary || this.summaryCurrency === 'USD') {
      this.equalToUsdAmount = null;
      return;
    }

    const order = this.orderSummary.data?.order;
    const payment = order?.payment ?? null;

    if (this.isCheckoutMode) {
      if (this.billingPreview?.chargeCurrency === 'USD') {
        this.equalToUsdAmount = this.billingPreview.chargeAmount;
        return;
      }

      this.equalToUsdAmount = null;
      return;
    }

    if (payment?.isPaid && payment?.amount) {
      this.equalToUsdAmount = payment.amount;
      return;
    }

    if (this.billingPreview?.chargeCurrency === 'USD') {
      this.equalToUsdAmount = this.billingPreview.chargeAmount;
      return;
    }

    if (!this.currencyExchangeRate) {
      this.equalToUsdAmount = null;
      return;
    }

    if (payment?.amount) {
      this.equalToUsdAmount = payment.amount;
      return;
    }

    this.equalToUsdAmount = (order?.totalAmount ?? 0) * this.currencyExchangeRate;
  }

  private get checkoutBaseSubtotalAmount(): number {
    if (!this.billingPreview || !('baseSubtotal' in this.billingPreview)) {
      return 0;
    }

    return this.billingPreview.baseSubtotal ?? 0;
  }

  private get checkoutDiscountAmount(): number {
    if (!this.billingPreview || !('discountAmount' in this.billingPreview)) {
      return 0;
    }

    return this.billingPreview.discountAmount ?? 0;
  }

}
